import { common, query } from '@kurtosys/ksys-app-template';
import {
	IBatchPromiseElement,
	IFundlistBody,
	TCommentaryResponse,
	TEntityType,
} from '@kurtosys/types/api/common/index.js';
import { TOrchestratePagination } from '@kurtosys/types/api/common/TOrchestratePagination.js';
import { IListDisclaimersRequestBody } from '@kurtosys/types/api/config/IListDisclaimersRequestBody.js';
import { IListFundListsRequestBody } from '@kurtosys/types/api/config/IListFundListsRequestBody.js';
import { IRetrieveClientConfigurationRequestBody } from '@kurtosys/types/api/config/IRetrieveClientConfigurationRequestBody.js';
import { IRetrieveUserConfigurationRequestBody } from '@kurtosys/types/api/config/IRetrieveUserConfigurationRequestBody.js';
import { TRetrieveClientConfigurationResponseBody } from '@kurtosys/types/api/config/TRetrieveClientConfigurationResponseBody.js';
import { TRetrieveUserConfigurationResponseBody } from '@kurtosys/types/api/config/TRetrieveUserConfigurationResponseBody.js';
import { IDatasetExecuteRequest } from '@kurtosys/types/api/dataset/IDatasetExecuteRequest.js';
import { IDatasetExecuteRequestConfiguration } from '@kurtosys/types/api/dataset/IDatasetExecuteRequestConfiguration.js';
import { IDatasetExecuteResponse } from '@kurtosys/types/api/dataset/IDatasetExecuteResponse.js';
import { TSearchDocumentsResponseBody } from '@kurtosys/types/api/documents/TSearchDocumentsResponseBody.js';
import {
	IDocument,
	IEntity,
	IFetchEntitiesOptions,
	ILinkedEntity,
	IListCommentariesRequestBody,
	ISearch,
	ISearchEntityRequest,
	ISearchGroupedEntityRequest,
} from '@kurtosys/types/api/search/index.js';
import { IGetArticleResponse, IListMyCoursesResponse } from '@kurtosys/types/api/wordpress/index.js';
import { IUser } from '@kurtosys/types/authentication/IUser.js';
import { IMergeOptions } from '@kurtosys/types/common/IMergeOptions.js';
import { TCourses } from '@kurtosys/types/learning/TCourses.js';
import {
	ErrorCode,
	IServiceResponse,
	TPortfolioEntityGetResponse,
	TPortfolioGetResponse,
	TScenarioGetResponse,
	TScenarioListResponse,
	TScenarioReportGetResponse,
	TScenarioReportListResponse,
} from '@kurtosys/types/portal/index.js';
import {
	IExecutionOptions,
	IQueryContext,
	IQueryContextConfiguration,
	IQueryContextConfigurationContentItems,
	IQueryContextConfigurationDocument,
	IQueryContextConfigurationEntity,
	IQueryContextConfigurationSavedSearchEntity,
	IQueryContextCulture,
	IQueryContextDataset,
	IQueryContextEntity,
	IQueryContextFundLists,
	IQueryContextGlobalSearchContent,
	IQueryContextInputQuery,
	IQueryContextLearning,
	IQueryContextLoggedInUser,
	IQueryContextPortalBase,
	IQueryContextPortfolio,
	IQueryContextScenario,
	IQueryContextScenarioReport,
	IQueryContextScenarioReports,
	IQueryContextScenarios,
	IQueryContextSearchEntities,
	IQueryKeyValue,
	IQueryOptions,
} from '@kurtosys/types/query/index.js';
import { GlobalSearchResponse } from '@kurtosys/types/wordpressGlobalSearch/GlobalSearchResponse.js';

import { IQueryContextClient } from './IQueryContextClient.js';
const replaceTextRecursive = common.commonUtils.replaceTextRecursive;

export class QueryContext {
	abortController: AbortController;
	constructor(public configuration: IQueryContextConfiguration, public seed?: IQueryContext) {}
	async fetch(
		client: IQueryContextClient,
		inputs: Record<string, any>,
		culture?: IQueryContextCulture,
		abortController?: AbortController,
	): Promise<IQueryContext> {
		this.abortController = abortController;
		const response: IQueryContext = {};

		const configuration = this.orchestrateConfiguration(this.configuration, inputs);

		const batchPromises: IBatchPromiseElement[] = [
			...this.getEntityPromises(response, client, configuration, inputs, culture),
			...this.getCommentaryPromises(response, client, configuration),
			...this.getDisclaimerPromises(response, client, configuration),
			...this.getDocumentPromises(response, client, configuration),
			...this.getFundListPromises(response, client, configuration),
			...this.getClientConfigurationPromises(response, client, configuration),
			...this.getUserConfigurationPromises(response, client, configuration),
			...this.getScenarioPromises(response, client, configuration, inputs),
			...this.getScenarioReportPromises(response, client, configuration, inputs),
			...this.getLearningPromises(response, client, configuration, inputs),
			...this.getLoggedInUserPromises(response, client, configuration, inputs),
			...this.getGlobalSearchPromises(response, client, configuration, inputs),
		];

		await common.commonUtils.batchExecutePromises(batchPromises);

		response.isPerformanceAllowed = this.getPerformanceAllowed(response);
		response.globalQueries = this.configuration.globalQueries;

		// These can rely on the previously fetched information so they need to stay here
		await this.loadPortfolio(response, client, configuration, inputs);
		await this.loadPortfolioEntity(response, client, configuration, inputs);
		await this.getEntitiesPromises(response, client, configuration, inputs, culture);
		await this.getDatasetPromises(response, client, configuration, inputs, culture);
		return response;
	}

	orchestrateConfiguration(configuration: IQueryContextConfiguration, inputs: Record<string, any> = {}) {
		// If there are multiple inputs then we would rather convert everything to a string once,
		// then replace it as a string search to avoid racking up the call stack
		try {
			let configurationString = JSON.stringify(configuration);
			for (const key of Object.keys(inputs)) {
				const inputKey = `{input:${key}}`;
				let inputValue = inputs[key];
				// If we try to join with undefined we are getting a "," returned
				if (common.commonUtils.isUndefined(inputValue)) {
					inputValue = '';
				}
				configurationString = configurationString.split(inputKey).join(inputValue);
			}
			const response = JSON.parse(configurationString);
			return response;
		} catch (ex) {
			// If the conversions fail for any reason we can do a deep recursion
			// This can fail if the configuration for some reason has a circular dependency, of the
			// input value is invalid when replacing into the string
			console.warn('orchestrateConfiguration: string replacement failed, moving to recursive', ex);
		}

		const workingConfiguration = common.commonUtils.deepMergeObjects({}, configuration);
		for (const key of Object.keys(inputs)) {
			const inputKey = `{input:${key}}`;
			let inputValue = inputs[key];
			// If we try to join with undefined we are getting a "," returned
			if (common.commonUtils.isUndefined(inputValue)) {
				inputValue = '';
			}
			replaceTextRecursive(workingConfiguration, inputKey, inputValue);
		}
		return workingConfiguration;
	}

	getPerformanceAllowed(response: IQueryContext) {
		const { performanceCheck } = this.configuration;
		if (performanceCheck) {
			const { start: startQueryOptions, end: endQueryOptions, useNowForEndDate, measure } = performanceCheck;
			if (measure && startQueryOptions && (endQueryOptions || useNowForEndDate)) {
				const { operator, period, value: expectedValue } = measure;
				const executionOptions: IExecutionOptions = {
					context: response,
				};
				const startQuery = new query.Query(startQueryOptions);
				const startDateRaw = startQuery.execute(executionOptions);
				let endDateRaw;
				if (useNowForEndDate) {
					endDateRaw = new Date().toUTCString();
				} else {
					const endQuery = new query.Query(endQueryOptions);
					endDateRaw = endQuery.execute(executionOptions);
				}
				const dateDifference = common.commonUtils.getDateDifference(endDateRaw, startDateRaw, period);
				let permissionAllowed = false;
				switch (operator) {
					case '>':
						permissionAllowed = dateDifference > expectedValue;
						break;
					case '>=':
						permissionAllowed = dateDifference >= expectedValue;
						break;
					default:
						break;
				}
				return permissionAllowed;
			}
		}
		// If no performance check is provided allow performance values
		return true;
	}

	getEntityPromisesFromSeed(response: IQueryContext) {
		const batchPromises: IBatchPromiseElement[] = [];
		if (this.seed && this.seed.entityByType) {
			// Has a seed value for the request
			response.entityByType = {};
			for (const entityType of Object.keys(this.seed.entityByType)) {
				const entityTypeSeedValue = this.seed.entityByType[entityType];
				batchPromises.push({
					identifier: { entityType },
					promiseFunc: async () => entityTypeSeedValue,
					responseFunc: (promiseResponse: IEntity, identifier: { entityType: TEntityType }) => {
						response.entityByType[identifier.entityType] = promiseResponse;
					},
				});
			}
		}
		return batchPromises;
	}

	getEntityPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
		inputs: Record<string, any>,
		culture?: IQueryContextCulture,
	) {
		const batchPromises: IBatchPromiseElement[] = [...this.getEntityPromisesFromSeed(response)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const { entityByType } = configuration;
			if (entityByType) {
				response.entityByType = {};
				for (const entityType of Object.keys(entityByType)) {
					const entityTypeConfiguration = entityByType[entityType] as IQueryContextConfigurationEntity;
					batchPromises.push({
						identifier: { entityType },
						promiseFunc: () =>
							this.fetchEntityForType(
								client,
								inputs,
								entityType as TEntityType,
								entityTypeConfiguration,
								culture,
							),
						responseFunc: (promiseResponse: IEntity, identifier: { entityType: TEntityType }) => {
							response.entityByType[identifier.entityType] = promiseResponse;
						},
					});
				}
			}
		}
		return batchPromises;
	}

	getEntitiesPromisesFromSeed(response: IQueryContext) {
		const batchPromises: IBatchPromiseElement[] = [];
		if (this.seed && this.seed.entities) {
			// Has a seed value for the request
			response.entities = {};
			for (const entityKey of Object.keys(this.seed.entities)) {
				const entitiesSeedValue = this.seed.entities[entityKey];
				batchPromises.push({
					identifier: { entityKey },
					promiseFunc: async () => entitiesSeedValue,
					responseFunc: (promiseResponse: any, identifier: { entityKey: string }) => {
						response.entities[identifier.entityKey] = promiseResponse;
					},
				});
			}
		}
		return batchPromises;
	}

	async getEntitiesPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
		inputs: Record<string, any>,
		culture?: IQueryContextCulture,
	) {
		const batchPromises: IBatchPromiseElement[] = [...this.getEntitiesPromisesFromSeed(response)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const { entities } = configuration;
			if (entities) {
				response.entities = {};
				const fetchOnInitializeEntities: IQueryContextEntity[] = entities.filter(
					(entity: IQueryContextEntity) => entity.fetchOnInitialize,
				);
				// Filter and prepare the linked entities
				const entitiesWithLinks = entities.filter((entity) => entity.linkedEntities?.length > 0);
				if (entitiesWithLinks?.length > 0) {
					// Find duplicated links across entities. This is not allowed due to the responses being overridden.
					const links: string[] = common.commonUtils.flatDeep(
						entitiesWithLinks.map((entityWithLinks) =>
							entityWithLinks.linkedEntities.map((linkedEntity) => linkedEntity.targetId),
						),
						Infinity,
					) as string[];
					const duplicateLinks = links.filter((item: string, index: number) => links.indexOf(item) !== index);
					if (duplicateLinks?.length > 0) {
						console.error(
							`CONFIGURATION ERROR: Target Ids (${duplicateLinks.join(
								', ',
							)}) are duplicated in Linked Entities. Please update the configuration.`,
						);
					}

					// Find linked fetchOnInitialize entities. This is not allowed as it could cause a circular reference under certain conditions.
					// The circular reference will only last about 1 level deep once the promise resolves.
					const initializedEntityLinkedReferences = links.reduce((references: string[], link: string) => {
						if (fetchOnInitializeEntities.findIndex((entity) => entity.id === link) > -1) {
							references.push(link);
						}
						return references;
					}, []);

					if (initializedEntityLinkedReferences?.length > 0) {
						console.error(
							`CONFIGURATION ERROR: Target Ids (${initializedEntityLinkedReferences.join(
								', ',
							)}) are referencing fetchOnInitialize entities. Please update the configuration.`,
						);
					}
				}
				// Only adds the entities that are configured to be fetched on initialize.
				for (const entity of fetchOnInitializeEntities) {
					batchPromises.push(
						this.getEntitiesBatchPromises(entity, response, client, configuration, inputs, culture),
					);
				}
			}
		}
		return common.commonUtils.batchExecutePromises(batchPromises);
	}

	getEntitiesBatchPromises(
		entity: IQueryContextEntity,
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
		inputs: Record<string, any>,
		culture?: IQueryContextCulture,
		linkedEntityConfig?: ILinkedEntity & { sourceId: string },
	) {
		return {
			identifier: { entityKey: entity.id },
			promiseFunc: () =>
				this.fetchEntities(client, entity.id, inputs, configuration, {
					inputs,
					culture: culture && culture.targetCulture,
					context: response,
				}),
			responseFunc: async (promiseResponse: any, identifier: { entityKey: string }) => {
				// Set the response.entities at the top of the function to prevent circular referencing of the linked entities.
				// Checks are in place to prevent a linked entity from firing again if there is already a response.
				response.entities[identifier.entityKey] = promiseResponse;

				this.applyEntityMapToLinkedEntities(
					response,
					linkedEntityConfig,
					identifier.entityKey,
					promiseResponse,
				);

				// Track entities that have already been executed and ignore.
				if (entity.linkedEntities) {
					const batchPromises: IBatchPromiseElement[] = [];
					for (const linkedEntity of entity.linkedEntities) {
						// Don't fire if the entity already has a promise. Avoid circular referencing.
						const hasExistingResponse = response.entities[linkedEntity.targetId];
						if (!hasExistingResponse) {
							const { entities } = configuration;
							// find the associated linkedEntity IQueryContextEntity
							const childEntity = entities.find((e) => e.id === linkedEntity.targetId);
							this.orchestrateLinkedEntitySearch(childEntity, linkedEntity, promiseResponse);

							// Create query to set the search prop and call self. Sets up batch for current list of linked entities.
							// Parent entity has already resolved.
							batchPromises.push(
								this.getEntitiesBatchPromises(
									childEntity,
									response,
									client,
									configuration,
									inputs,
									culture,
									Object.assign(linkedEntity, { sourceId: identifier.entityKey }),
								),
							);
						}
					}
					await common.commonUtils.batchExecutePromises(batchPromises);
				}
			},
		};
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	applyEntityMapToLinkedEntities(
		response: IQueryContext,
		linkedEntityParentConfig: ILinkedEntity & { sourceId: string },
		entityKey: string,
		promiseResponse: any,
	) {
		// If the parent had linked entities, determine which child entities belong to it and add it to its entity map
		if (linkedEntityParentConfig) {
			// The current batch of entities
			const entities = response.entities[entityKey];
			const parentEntityKey = linkedEntityParentConfig.sourceId;
			// The parent entities that these entities might have a relation with
			const parentEntities = response.entities[parentEntityKey];

			// Ensure that there are entities to work on
			if (parentEntities.total > 0 && entities.total > 0) {
				const { sourceIdentifierType, sourceProperty, targetIdentifierType, targetProperty } =
					linkedEntityParentConfig;
				for (const parentEntity of parentEntities.values) {
					// Source Identifier will hold the value from the parent that the entities should match based on the property that links them up
					const sourceIdentifierValue = this.resolveLinkedEntitiesConfigurationValues(
						parentEntity,
						sourceIdentifierType,
						sourceProperty,
					);
					for (const childEntity of entities.values) {
						// Target Identifier will be the value from the entity that the source should match
						const targetIdentifierValue = this.resolveLinkedEntitiesConfigurationValues(
							childEntity,
							targetIdentifierType,
							targetProperty,
						);
						// need to handle cardinality n values
						if (this.identifiersMatch(targetIdentifierValue, sourceIdentifierValue)) {
							// First ensure the mapping keys are setup
							this.setupMappingKeysForEntities(parentEntities);
							this.setupMappingKeysForEntities(entities);
							// Then populate them
							this.populateEntitiesMaps(parentEntities, childEntity, parentEntity.entityId, entityKey);
							this.populateEntitiesMaps(entities, parentEntity, childEntity.entityId, parentEntityKey);
						}
					}
				}
			}
		}
	}

	identifiersMatch(targetIdentifier: any | any[], sourceIdentifier: any | any[]): boolean {
		if (common.commonUtils.isArrayLike(targetIdentifier) && !common.commonUtils.isArrayLike(sourceIdentifier)) {
			return targetIdentifier.includes(sourceIdentifier);
		}
		if (common.commonUtils.isArrayLike(sourceIdentifier) && !common.commonUtils.isArrayLike(targetIdentifier)) {
			return sourceIdentifier.includes(targetIdentifier);
		}
		if (common.commonUtils.isArrayLike(targetIdentifier) && common.commonUtils.isArrayLike(sourceIdentifier)) {
			return targetIdentifier.some((value) => sourceIdentifier.includes(value));
		}
		if (!common.commonUtils.isArrayLike(targetIdentifier) && !common.commonUtils.isArrayLike(sourceIdentifier)) {
			return targetIdentifier === sourceIdentifier;
		}
		console.warn(
			`identifiersMatch: Returned false for target value: ${targetIdentifier} & source value: ${sourceIdentifier}`,
		);
		return false;
	}

	populateEntitiesMaps(
		targetEntities: IQueryContextSearchEntities,
		linkedEntity: IEntity,
		targetEntityId: number,
		entityKey: string,
	) {
		// Populate the entitiesById
		const parentEntitiesById = targetEntities.entitiesById;
		// If the ById map does not have the entity in the map add it
		if (!parentEntitiesById.has(linkedEntity.entityId)) {
			parentEntitiesById.set(linkedEntity.entityId, linkedEntity);
		}
		// Populate the entityMap
		const targetEntitiesMap = targetEntities.entityMap;
		if (targetEntitiesMap) {
			if (!targetEntitiesMap.has(targetEntityId)) {
				targetEntitiesMap.set(targetEntityId, {});
			}
			const entityMap = targetEntitiesMap.get(targetEntityId);
			if (!entityMap[entityKey]) {
				targetEntitiesMap.set(targetEntityId, { ...entityMap, [entityKey]: new Set() });
			}
			targetEntitiesMap.get(targetEntityId)[entityKey].add(linkedEntity.entityId);
		}
	}

	setupMappingKeysForEntities(entities: IQueryContextSearchEntities) {
		if (common.commonUtils.isNullOrUndefined(entities.entitiesById)) {
			entities.entitiesById = new Map();
		}
		if (common.commonUtils.isNullOrUndefined(entities.entityMap)) {
			entities.entityMap = new Map();
		}
	}

	resolveLinkedEntitiesConfigurationValues(
		entity: IEntity,
		type: 'clientCode' | 'property',
		property: string,
	): any | any[] {
		if (type === 'clientCode') {
			return entity.clientCode;
		}
		return entity.properties_pub[property] && entity.properties_pub[property].value;
	}

	orchestrateLinkedEntitySearch(entity: IQueryContextEntity, linkedEntity: ILinkedEntity, response: any) {
		const { sourceIdentifierType, sourceProperty, targetIdentifierType, targetProperty } = linkedEntity;
		const { values } = response;
		const { requestBody } = entity;
		const { search } = requestBody;
		const responseValues = values?.reduce((responseValues: string[], value: any) => {
			if (sourceIdentifierType === 'clientCode') {
				responseValues.push(value.clientCode);
				return responseValues;
			}
			const propertyValue: string | string[] = value.properties_pub[sourceProperty]?.value;
			if (sourceIdentifierType === 'property' && propertyValue) {
				if (Array.isArray(propertyValue)) {
					responseValues.push(...propertyValue);
				} else {
					responseValues.push(propertyValue);
				}
			}
			return responseValues;
		}, []);

		// Remove duplicates to clean up the search values
		const uniqueResponseValues = responseValues.filter((c, index) => responseValues.indexOf(c) === index);

		const property = targetIdentifierType === 'property' ? targetProperty : 'clientCode';
		// Find existing search target property to append values if the existing is matchtype is MATCH to avoid any weird edge cases.
		const targetIndex = (search as ISearch[]).findIndex(
			(searchItem: ISearch) => searchItem.property === property && searchItem.matchtype === 'MATCH',
		);
		if (targetIndex > -1) {
			search[targetIndex].values.push(uniqueResponseValues);
		} else {
			search.push({
				property,
				matchtype: 'MATCH',
				values: uniqueResponseValues,
			} as ISearch);
		}
	}

	getScenarioPromisesFromSeed(response: IQueryContext) {
		const batchPromises: IBatchPromiseElement[] = [];
		if (this.seed && this.seed.scenario) {
			// Has a seed value for the request
			const scenarioSeedValue = this.seed.scenario;
			batchPromises.push({
				identifier: {},
				promiseFunc: async () => scenarioSeedValue,
				responseFunc: (promiseResponse: TScenarioGetResponse) => {
					response.scenario = promiseResponse;
				},
			});
		}
		return batchPromises;
	}

	getScenarioPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
		inputs: Record<string, any>,
	) {
		const batchPromises: IBatchPromiseElement[] = [...this.getScenarioPromisesFromSeed(response)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const { scenario } = configuration;
			if (scenario) {
				batchPromises.push({
					identifier: {},
					promiseFunc: async () => this.fetchScenario(client, inputs, scenario),
					responseFunc: (promiseResponse: IServiceResponse<TScenarioGetResponse>) => {
						if (promiseResponse && promiseResponse.value) {
							response.scenario = promiseResponse.value;
						}
					},
				});
			}
		}
		return batchPromises;
	}

	getScenarioReportPromisesFromSeed(response: IQueryContext) {
		const batchPromises: IBatchPromiseElement[] = [];
		if (this.seed && this.seed.scenarioReport) {
			// Has a seed value for the request
			const scenarioReportSeedValue = this.seed.scenarioReport;
			batchPromises.push({
				identifier: {},
				promiseFunc: async () => scenarioReportSeedValue,
				responseFunc: (promiseResponse: TScenarioReportGetResponse) => {
					response.scenarioReport = promiseResponse;
				},
			});
		}
		return batchPromises;
	}

	getLearningPromisesFromSeed(response: IQueryContext) {
		const batchPromises: IBatchPromiseElement[] = [];
		if (this.seed && this.seed.article) {
			// Has a seed value for the request
			const articleSeedValue = this.seed.article;
			batchPromises.push({
				identifier: {},
				promiseFunc: async () => articleSeedValue,
				responseFunc: (promiseResponse: IGetArticleResponse) => {
					response.article = promiseResponse;
				},
			});
		}
		if (this.seed && this.seed.courses) {
			// Has a seed value for the request
			const coursesSeedValue = this.seed.courses;
			batchPromises.push({
				identifier: {},
				promiseFunc: async () => coursesSeedValue,
				responseFunc: (promiseResponse: TCourses) => {
					response.courses = promiseResponse;
				},
			});
		}
		return batchPromises;
	}

	getGlobalSearchPromisesFromSeed(response: IQueryContext) {
		const batchPromises: IBatchPromiseElement[] = [];
		if (this.seed && this.seed.wordpressGlobalSearch) {
			// Has a seed value for the request
			const articleSeedValue = this.seed.wordpressGlobalSearch;
			batchPromises.push({
				identifier: {},
				promiseFunc: async () => articleSeedValue,
				responseFunc: (promiseResponse: GlobalSearchResponse) => {
					response.wordpressGlobalSearch = promiseResponse;
				},
			});
		}
		return batchPromises;
	}

	getLoggedInUserPromisesFromSeed(response: IQueryContext) {
		const batchPromises: IBatchPromiseElement[] = [];
		if (this.seed && this.seed.loggedInUser) {
			// Has a seed value for the request
			const loggedInUserSeedValue = this.seed.loggedInUser;
			batchPromises.push({
				identifier: {},
				promiseFunc: async () => loggedInUserSeedValue,
				responseFunc: (promiseResponse: IUser) => {
					response.loggedInUser = promiseResponse;
				},
			});
		}
		return batchPromises;
	}

	getScenarioReportPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
		inputs: Record<string, any>,
	) {
		const batchPromises: IBatchPromiseElement[] = [...this.getScenarioReportPromisesFromSeed(response)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const { scenarioReport } = configuration;
			if (scenarioReport) {
				batchPromises.push({
					identifier: {},
					promiseFunc: async () => this.fetchScenarioReport(client, inputs, scenarioReport),
					responseFunc: (promiseResponse: IServiceResponse<TScenarioReportGetResponse>) => {
						if (promiseResponse && promiseResponse.value) {
							response.scenarioReport = promiseResponse.value;
						}
					},
				});
			}
		}
		return batchPromises;
	}

	getLearningPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
		inputs: Record<string, any>,
	) {
		const batchPromises: IBatchPromiseElement[] = [...this.getLearningPromisesFromSeed(response)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const { learning } = configuration;
			if (learning) {
				batchPromises.push(
					{
						identifier: {},
						promiseFunc: async () => this.fetchArticle(client, inputs, learning),
						responseFunc: (promiseResponse: IServiceResponse<IGetArticleResponse>) => {
							if (promiseResponse && promiseResponse.value) {
								response.article = promiseResponse.value;
							}
						},
					},
					{
						identifier: {},
						promiseFunc: async () => this.fetchCourses(client, inputs, learning),
						responseFunc: (promiseResponse: IListMyCoursesResponse) => {
							if (promiseResponse && promiseResponse.courses) {
								response.courses = promiseResponse.courses;
							}
						},
					},
				);
			}
		}
		return batchPromises;
	}

	getGlobalSearchPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
		inputs: Record<string, any>,
	) {
		const batchPromises: IBatchPromiseElement[] = [...this.getGlobalSearchPromisesFromSeed(response)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const { wordpressGlobalSearch } = configuration;
			if (wordpressGlobalSearch) {
				batchPromises.push({
					identifier: {},
					promiseFunc: async () =>
						this.fetchWordpressGlobalSearchContent(client, inputs, wordpressGlobalSearch),
					responseFunc: (promiseResponse: IServiceResponse<GlobalSearchResponse>) => {
						if (promiseResponse && promiseResponse.value) {
							response.wordpressGlobalSearch = promiseResponse.value;
						}
					},
				});
			}
		}
		return batchPromises;
	}

	getLoggedInUserPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
		inputs: Record<string, any>,
	) {
		const batchPromises: IBatchPromiseElement[] = [...this.getLoggedInUserPromisesFromSeed(response)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const { loggedInUser } = configuration;
			if (loggedInUser) {
				batchPromises.push({
					identifier: {},
					promiseFunc: async () => this.fetchLoggedInUser(client, inputs, loggedInUser),
					responseFunc: (promiseResponse: IUser) => {
						if (promiseResponse) {
							response.loggedInUser = promiseResponse;
						}
					},
				});
			}
		}
		return batchPromises;
	}

	loadPortfolioFromSeed(response: IQueryContext) {
		let hasSeedValue = false;
		if (this.seed && this.seed.portfolio) {
			// Has a seed value for the request
			response.portfolio = this.seed.portfolio;
			hasSeedValue = true;
		}
		return hasSeedValue;
	}

	async loadPortfolio(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
		inputs: Record<string, any>,
	) {
		const hasSeedValue = this.loadPortfolioFromSeed(response);
		// If there are batch promises from the seed then don't try to fetch information
		if (!hasSeedValue) {
			const { portfolio: portfolioConfiguration } = configuration;
			if (portfolioConfiguration) {
				if (response && response.scenario) {
					// If there is a scenario with a matching portfolio then return that
					const { portfolios } = response.scenario;
					if (portfolios) {
						const idQuery = new query.Query(portfolioConfiguration.id);
						const id = idQuery.execute({ inputs });
						const portfolio = portfolios.find((portfolio: TPortfolioGetResponse) =>
							this.idEqual(id, portfolio.portfolioId),
						);
						if (portfolio) {
							response.portfolio = portfolio;
						}
						return;
					}
				}
				const promiseResponse = await this.fetchPortfolio(client, inputs, portfolioConfiguration);
				if (promiseResponse && promiseResponse.value) {
					response.portfolio = promiseResponse.value;
				}
			}
		}
	}

	idEqual(id: number | string, existingId: number) {
		if (typeof id === 'string') {
			return !common.commonUtils.isNullOrUndefined(existingId) && id === existingId.toString();
		}
		return id === existingId;
	}

	loadPortfolioEntityFromSeed(response: IQueryContext) {
		let hasSeedValue = false;
		if (this.seed && this.seed.portfolioEntity) {
			// Has a seed value for the request
			response.portfolioEntity = this.seed.portfolioEntity;
			hasSeedValue = true;
		}
		return hasSeedValue;
	}

	async loadPortfolioEntity(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
		inputs: Record<string, any>,
	) {
		const hasSeedValue = this.loadPortfolioEntityFromSeed(response);
		// If there are batch promises from the seed then don't try to fetch information
		if (hasSeedValue) {
			const { portfolioEntity: portfolioEntityConfiguation } = configuration;
			if (portfolioEntityConfiguation) {
				if (response && response.portfolio) {
					// If there is a scenario with a matching portfolio then return that
					const { entities } = response.portfolio;
					if (entities) {
						const idQuery = new query.Query(portfolioEntityConfiguation.id);
						const id = idQuery.execute({ inputs });
						const portfolioEntity = entities.find((portfolioEntity: TPortfolioEntityGetResponse) =>
							this.idEqual(id, portfolioEntity.portfolioEntityId),
						);
						if (portfolioEntity) {
							response.portfolioEntity = portfolioEntity;
						}
						return;
					}
				}

				const promiseResponse = await this.fetchPortfolioEntity(client, inputs, portfolioEntityConfiguation);
				if (promiseResponse && promiseResponse.value) {
					response.portfolioEntity = promiseResponse.value;
				}
			}
		}
	}

	getDocumentPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
	) {
		const collectionKey = 'globalDocuments';
		const batchPromises: IBatchPromiseElement[] = [...this.getCollectionPromisesFromSeed(response, collectionKey)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const collection: IQueryContextConfigurationDocument[] | undefined = configuration[collectionKey];
			const previousDocumentsByClientCode: Set<string> = new Set();
			if (collection && Array.isArray(collection)) {
				for (const documentQuery of collection) {
					batchPromises.push({
						identifier: collectionKey,
						promiseFunc: async () => {
							return client.documents.search.execute({ body: documentQuery, signal: this.abortController?.signal });
						},
						responseFunc: (promiseResponse: TSearchDocumentsResponseBody, identifier: string) => {
							response = this.orchestrateSearchDocumentResponse(
								promiseResponse,
								response,
								previousDocumentsByClientCode,
							);
						},
					});
				}
			}
		}
		return batchPromises;
	}

	orchestrateSearchDocumentResponse(
		searchDocumentResponse: TSearchDocumentsResponseBody,
		queryContext: IQueryContext,
		previousDocumentsByClientCode: Set<string>,
	): IQueryContext {
		if (searchDocumentResponse) {
			if (searchDocumentResponse.entities) {
				if (!queryContext.documentEntities) {
					queryContext.documentEntities = {};
				}
				queryContext.documentEntities = {
					...queryContext.documentEntities,
					...searchDocumentResponse.entities,
				};
			}
			if (searchDocumentResponse.joinMap) {
				if (!queryContext.documentJoinMap) {
					queryContext.documentJoinMap = {};
				}
				queryContext.documentJoinMap = {
					...queryContext.documentJoinMap,
					...searchDocumentResponse.joinMap,
				};
			}
			if (!queryContext['globalDocuments']) {
				queryContext['globalDocuments'] = [];
			}
			if (searchDocumentResponse && searchDocumentResponse.values) {
				const { values: documents } = searchDocumentResponse;
				const uniqueDocuments = documents.filter(
					(document: IDocument) => !previousDocumentsByClientCode.has(document.clientCode),
				);
				uniqueDocuments.forEach((document) => {
					const { clientCode } = document;
					previousDocumentsByClientCode.add(clientCode);
				});
				queryContext['globalDocuments'].push(...uniqueDocuments);
			}
		}
		return queryContext;
	}

	getCollectionPromisesFromSeed(
		response: IQueryContext,
		collectionKey: 'globalCommentaries' | 'globalDocuments' | 'globalDisclaimers',
	): IBatchPromiseElement[] {
		const batchPromises: IBatchPromiseElement[] = [];
		const collection = this.seed && this.seed[collectionKey];
		if (collection) {
			batchPromises.push({
				identifier: collectionKey,
				promiseFunc: async () => collection,
				responseFunc: (promiseResponse: any[], identifier: string) => {
					if (promiseResponse) {
						if (!response[identifier]) {
							response[identifier] = [];
						}
						response[identifier].push(...promiseResponse);
					}
				},
			});
		}
		return batchPromises;
	}

	getCommentaryPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
	) {
		const collectionKey = 'globalCommentaries';
		const batchPromises: IBatchPromiseElement[] = [...this.getCollectionPromisesFromSeed(response, collectionKey)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const collection: IQueryContextConfigurationContentItems[] | undefined = configuration[collectionKey];
			if (collection && Array.isArray(collection)) {
				for (const contentItemQuery of collection) {
					const { cultures, global, types } = contentItemQuery;
					batchPromises.push({
						identifier: collectionKey,
						promiseFunc: async () => {
							const body: IListCommentariesRequestBody = {};
							if (types && types.length > 0) {
								body.commentaryType = types;
							}
							if (cultures && cultures.length > 0) {
								body.culture = cultures;
							}
							if (typeof global === 'boolean') {
								body.global = global;
							}
							return client.commentaries.list.execute({ body, signal: this.abortController?.signal });
						},
						responseFunc: (promiseResponse: TCommentaryResponse[], identifier: string) => {
							if (promiseResponse) {
								if (!response[identifier]) {
									response[identifier] = [];
								}
								response[identifier].push(...promiseResponse);
							}
						},
					});
				}
			}
		}
		return batchPromises;
	}

	getDisclaimerPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
	) {
		const collectionKey = 'globalDisclaimers';
		const batchPromises: IBatchPromiseElement[] = [...this.getCollectionPromisesFromSeed(response, collectionKey)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const collection: IQueryContextConfigurationContentItems[] | undefined = configuration[collectionKey];
			if (collection && Array.isArray(collection)) {
				for (const contentItemQuery of collection) {
					const { cultures, global, types } = contentItemQuery;
					batchPromises.push({
						identifier: collectionKey,
						promiseFunc: async () => {
							const body: IListDisclaimersRequestBody = {};
							if (types && types.length > 0) {
								body.disclaimerType = types;
							}
							if (cultures && cultures.length > 0) {
								body.culture = cultures;
							}
							if (typeof global === 'boolean') {
								body.global = global;
							}
							return client.disclaimers.list.execute({ body, signal: this.abortController?.signal });
						},
						responseFunc: (promiseResponse: TCommentaryResponse[], identifier: string) => {
							if (promiseResponse) {
								if (!response[identifier]) {
									response[identifier] = [];
								}
								response[identifier].push(...promiseResponse);
							}
						},
					});
				}
			}
		}
		return batchPromises;
	}

	getFundListPromisesFromSeed(response: IQueryContext) {
		const batchPromises: IBatchPromiseElement[] = [];
		if (this.seed && this.seed.fundListMapping) {
			batchPromises.push({
				identifier: 'fundLists',
				promiseFunc: async () => this.seed.fundListMapping,
				responseFunc: (promiseResponse: IQueryContextFundLists) => {
					response.fundListMapping = promiseResponse;
				},
			});
		}
		return batchPromises;
	}

	getFundListPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
	) {
		const batchPromises: IBatchPromiseElement[] = [...this.getFundListPromisesFromSeed(response)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const options = configuration.fundLists;
			if (
				options &&
				options.lists &&
				Array.isArray(options.lists) &&
				options.lists.length > 0 &&
				client &&
				client.fundLists &&
				client.fundLists.list
			) {
				const batchPromise: IBatchPromiseElement = {
					identifier: 'fundLists',
					promiseFunc: async () => {
						const body: IListFundListsRequestBody = {};
						const { lists } = options;
						if (lists) {
							body.listName = lists;
						}
						return client.fundLists.list.execute({ body, signal: this.abortController?.signal });
					},
					responseFunc: (promiseResponse: IFundlistBody[]) => {
						if (promiseResponse && Array.isArray(promiseResponse)) {
							const fundLists: IQueryContextFundLists = {};
							for (const fundListBody of promiseResponse) {
								const { listName, funds } = fundListBody;
								if (!fundLists[listName]) {
									fundLists[listName] = {};
								}
								if (funds && funds.length > 0) {
									for (const fund of funds) {
										if (typeof fund === 'string') {
											fundLists[listName][fund] = true;
										} else {
											fundLists[listName][fund.clientCode] = true;
										}
									}
								}
							}
							response.fundListMapping = fundLists;
						}
					},
				};
				batchPromises.push(batchPromise);
			}
		}
		return batchPromises;
	}
	//

	getDatasetPromisesFromSeed(response: IQueryContext) {
		const batchPromises: IBatchPromiseElement[] = [];
		if (this.seed && this.seed.datasets) {
			batchPromises.push({
				identifier: 'dataset',
				promiseFunc: async () => this.seed.fundListMapping,
				responseFunc: (promiseResponse: { [key: string]: IDatasetExecuteResponse }) => {
					response.datasets = promiseResponse;
				},
			});
		}
		return batchPromises;
	}

	async getDatasetPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
		inputs: Record<string, any>,
		culture?: IQueryContextCulture,
	) {
		const batchPromises: IBatchPromiseElement[] = [...this.getDatasetPromisesFromSeed(response)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const { datasets } = configuration;
			if (datasets) {
				response.datasets = {};
				for (const dataset of datasets) {
					// only process dataset requests that are flagged to be fetched when the application initializes
					if (dataset.fetchOnInitialize) {
						batchPromises.push(this.getDatasetsBatchPromises(dataset, response, client, inputs, culture));
					}
				}
			}
		}
		return common.commonUtils.batchExecutePromises(batchPromises);
	}

	getDatasetsBatchPromises(
		dataset: IQueryContextDataset,
		response: IQueryContext,
		client: IQueryContextClient,
		inputs: Record<string, any>,
		culture?: IQueryContextCulture,
	) {
		const { datasetKey } = dataset;
		const executionOptions = {
			inputs,
			culture: culture && culture.targetCulture,
			context: response,
		};

		return {
			identifier: datasetKey,
			promiseFunc: () => this.fetchDataset(client, dataset, executionOptions),
			responseFunc: async (promiseResponse: any, identifier: string) => {
				response.datasets[identifier] = promiseResponse.value;
			},
		};
	}

	getClientConfigurationPromisesFromSeed(response: IQueryContext) {
		const batchPromises: IBatchPromiseElement[] = [];
		const clientConfigurations = this.seed && this.seed.clientConfigurations;
		if (clientConfigurations) {
			for (const code of Object.keys(clientConfigurations)) {
				batchPromises.push({
					identifier: code,
					promiseFunc: async () => clientConfigurations[code],
					responseFunc: (promiseResponse: any, identifier: string) => {
						response.clientConfigurations[identifier] = promiseResponse.value;
					},
				});
			}
		}
		return batchPromises;
	}

	getClientConfigurationPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
	) {
		const batchPromises: IBatchPromiseElement[] = [...this.getClientConfigurationPromisesFromSeed(response)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const clientConfigurations: string[] = configuration.clientConfigurations;
			if (clientConfigurations && clientConfigurations.length > 0) {
				const prefix = 'clientConfiguration|';
				for (const code of clientConfigurations) {
					const batchPromise: IBatchPromiseElement = {
						identifier: `${prefix}${code}`,
						promiseFunc: async () => {
							const body: IRetrieveClientConfigurationRequestBody = {
								code,
							};
							return client.config.clientConfiguration.retrieve.execute({ body, signal: this.abortController?.signal });
						},
						responseFunc: (
							promiseResponse: TRetrieveClientConfigurationResponseBody,
							identifier: string,
						) => {
							if (promiseResponse && identifier.startsWith(prefix)) {
								if (!response.clientConfigurations) {
									response.clientConfigurations = {};
								}
								const code = identifier.substr(prefix.length);
								response.clientConfigurations[code] = promiseResponse;
							}
						},
					};
					batchPromises.push(batchPromise);
				}
			}
		}
		return batchPromises;
	}

	getUserConfigurationPromisesFromSeed(response: IQueryContext) {
		const batchPromises: IBatchPromiseElement[] = [];
		const userConfigurations = this.seed && this.seed.userConfigurations;
		if (userConfigurations) {
			for (const code of Object.keys(userConfigurations)) {
				batchPromises.push({
					identifier: code,
					promiseFunc: async () => userConfigurations[code],
					responseFunc: (promiseResponse: any, identifier: string) => {
						response.userConfigurations[identifier] = promiseResponse;
					},
				});
			}
		}
		return batchPromises;
	}

	getUserConfigurationPromises(
		response: IQueryContext,
		client: IQueryContextClient,
		configuration: IQueryContextConfiguration,
	) {
		const batchPromises: IBatchPromiseElement[] = [...this.getUserConfigurationPromisesFromSeed(response)];
		// If there are batch promises from the seed then don't try to fetch information
		if (batchPromises.length === 0) {
			const userConfigurations: string[] = configuration.userConfigurations;
			if (userConfigurations && userConfigurations.length > 0) {
				const prefix = 'userConfiguration|';
				for (const code of userConfigurations) {
					const batchPromise: IBatchPromiseElement = {
						identifier: `${prefix}${code}`,
						promiseFunc: async () => {
							const body: IRetrieveUserConfigurationRequestBody = {
								code,
							};
							return client.config.userConfiguration.retrieve.execute({ body, signal: this.abortController?.signal });
						},
						responseFunc: (promiseResponse: TRetrieveUserConfigurationResponseBody, identifier: string) => {
							if (promiseResponse && identifier.startsWith(prefix)) {
								if (!response.userConfigurations) {
									response.userConfigurations = {};
								}
								const code = identifier.substr(prefix.length);
								response.userConfigurations[code] = promiseResponse;
							}
						},
					};
					batchPromises.push(batchPromise);
				}
			}
		}
		return batchPromises;
	}

	async fetchEntityForType(
		client: IQueryContextClient,
		inputs: Record<string, any>,
		entityType: TEntityType,
		entityTypeConfiguration: IQueryContextConfigurationEntity,
		culture?: IQueryContextCulture,
	) {
		if (entityTypeConfiguration) {
			const entitiesResponse = await this.fetchEntitiesForType(
				client,
				inputs,
				entityType,
				entityTypeConfiguration,
				culture,
			);
			if (entitiesResponse && entitiesResponse.values && entitiesResponse.values.length > 0) {
				return entitiesResponse.values[0];
			}
		}
	}

	async fetchEntitiesForType(
		client: IQueryContextClient,
		inputs: Record<string, any>,
		entityType: TEntityType,
		entityTypeConfiguration: IQueryContextConfigurationEntity,
		culture?: IQueryContextCulture,
	) {
		if (entityTypeConfiguration) {
			const {
				inputKey,
				clientCodeProperty,
				include: explicitInclude,
				searchEntityRequest = {},
				searchEntityRequestOptions = {},
				savedSearchEntity,
				fetchOptions,
			} = entityTypeConfiguration;

			if (savedSearchEntity) {
				return await this.fetchSavedSearchEntity(client, savedSearchEntity, inputs, culture);
			}

			// if an inputKey has been defined, and no value has been provided - we will not attempt the entity search.
			const clientCode = inputs[inputKey] || '';
			if (!common.commonUtils.isNullOrWhitespace(inputKey) && common.commonUtils.isNullOrWhitespace(clientCode)) {
				return;
			}

			const include: ISearchEntityRequest['include'] = explicitInclude || searchEntityRequest.include || {};
			let workingSearch: ISearch[] = searchEntityRequest.search;
			if (clientCodeProperty && inputKey) {
				const { searchMergeType = 'replaceValues' } = searchEntityRequestOptions;
				const search: ISearch[] = [
					{
						property: clientCodeProperty,
						matchtype: 'MATCH',
						values: [clientCode],
					},
				];
				if (searchMergeType === 'keepValues') {
					const mergeOptions: IMergeOptions = {
						arrayMergeStrategy: 'DeepMergeByKeyOrAppend',
						getKeyFn: (item: any) => item.property || item,
					};
					const merged = common.commonUtils.deepMergeObjectsWithOptions(
						mergeOptions,
						{ search: workingSearch },
						{ search },
					);
					if (merged && merged.search) {
						workingSearch = merged.search;
					}
				} else {
					workingSearch = search;
				}
			}
			let translationOptions: ISearchEntityRequest = {};
			if (culture) {
				const { sourceCulture, targetCulture } = culture;
				if (sourceCulture && targetCulture) {
					translationOptions = {
						sourceCulture,
						culture: targetCulture,
						translate: true,
					};
				}
			}
			const requestBody: ISearchEntityRequest = this.cleanSearchEntityRequestBody(
				{
					...translationOptions,
					...{
						type: entityType,
					},
					...searchEntityRequest,
					...{
						include,
						search: workingSearch,
					},
				},
				inputs,
				fetchOptions,
			);

			const response = await client.entity.search.execute({
				baseUrl: client.options.baseUrl,
				body: requestBody,
				signal: this.abortController?.signal,
			});
			return response;
		}
	}

	async fetchEntities(
		client: IQueryContextClient,
		id: string,
		staticInputs: Record<string, any>,
		configuration: IQueryContextConfiguration,
		executionOptions: IExecutionOptions,
		overrideFetchOptions?: IFetchEntitiesOptions,
	) {
		const { entities = [] } = configuration;
		const queryContextEntity = entities.find((entity: IQueryContextEntity) => entity.id === id);
		if (queryContextEntity) {
			let workingQueryContextEntity = queryContextEntity;
			const fetchOptions = common.commonUtils.deepMergeObjects(
				{},
				workingQueryContextEntity.fetchOptions || {},
				overrideFetchOptions,
			);
			if (fetchOptions) {
				const { staticEntityOverride, staticEntityOverrideArrayType: staticEntityOverrideType = 'keepValues' } =
					fetchOptions;
				if (staticEntityOverride) {
					let mergeOptions;
					if (staticEntityOverrideType === 'replaceValues') {
						mergeOptions = { arrayMergeStrategy: 'DeepMerge' };
					} else {
						mergeOptions = {
							arrayMergeStrategy: 'DeepMergeByKeyOrAppend',
							getKeyFn: (item: any) => item.property || item,
						};
					}
					workingQueryContextEntity = common.commonUtils.deepMergeObjectsWithOptions(
						mergeOptions,
						workingQueryContextEntity,
						staticEntityOverride,
					);
				}
			}
			const {
				type,
				inputs: inputQueryList = [],
				requestBody,
				savedSearchEntity,
			} = workingQueryContextEntity;
			let {
				requestBodyQueries,
			} = workingQueryContextEntity;
			// configuration stores input queries as an array, in order for Studio UI to display input configs correctly, but work with keyValue Record<string, any>'s.
			const inputQueryKeyValue = inputQueryList.reduce(
				(inputQueries: IQueryKeyValue, inputQuery: IQueryContextInputQuery) => {
					if (!inputQueries[inputQuery.key]) {
						inputQueries[inputQuery.key] = inputQuery.query;
					}
					return inputQueries;
				},
				{} as IQueryKeyValue,
			);
			const dependencies = { queryClass: query.Query };

			requestBodyQueries = this.orchestratePaginationQueries({
				propertyKey: 'limit',
				inputKey: 'ksys-pagination-page-size',
				queryObject: requestBodyQueries,
				staticObject: requestBody,
			});

			requestBodyQueries = this.orchestratePaginationQueries({
				propertyKey: 'start',
				inputKey: 'ksys-pagination-record-index',
				queryObject: requestBodyQueries,
				staticObject: requestBody,
			});

			const dynamicInputs = query.queryUtils.queryHelper.convertQueryKeyValue(
				dependencies,
				inputQueryKeyValue,
				executionOptions,
			);
			const inputs = common.commonUtils.deepMergeObjects(staticInputs || {}, dynamicInputs || {});

			if (savedSearchEntity) {
				return await this.fetchSavedSearchEntity(client, savedSearchEntity, inputs);
			}

			let workingRequestBody = requestBody || {};
			if (requestBodyQueries) {
				const queryResults = query.queryUtils.queryHelper.convertQueryMime(
					dependencies,
					requestBodyQueries,
					executionOptions,
				);
				const requestBodyWithValidSearchItems = this.filterInvalidQueryResults(queryResults);
				workingRequestBody = common.commonUtils.deepMergeObjects(
					requestBody || {},
					requestBodyWithValidSearchItems,
				);
			}
			const cleanRequestBody = this.cleanSearchEntityRequestBody(workingRequestBody, inputs, fetchOptions);
			if (type === 'grouped') {
				return await client.entity.searchGrouped.execute({
					baseUrl: client.options.baseUrl,
					body: cleanRequestBody as ISearchGroupedEntityRequest,
					signal: this.abortController?.signal,
				});
			} else {
				return await client.entity.search.execute({
					baseUrl: client.options.baseUrl,
					body: cleanRequestBody as ISearchEntityRequest,
					signal: this.abortController?.signal,
				});
			}
		}
	}

	orchestratePaginationQueries<TStaticObj, TQueryObj>(options: TOrchestratePagination<TStaticObj, TQueryObj>) {
		const { propertyKey, inputKey, staticObject, queryObject } = options;

		// if pagination limit property has not been defined, skip the update for all properties.
		if ((!staticObject || !staticObject['limit']) && (!queryObject || !queryObject['limit'])) {
			return queryObject; // return unmodified
		}

		const workingQueryObject = structuredClone(queryObject);
		const paginationQuery: IQueryOptions = {
			queryOptionsType: 'input',
			key: inputKey,
			placeholder: 0,
		}
		if (staticObject && staticObject[propertyKey]) {
			paginationQuery.placeholder = staticObject[propertyKey];
		}
		if (workingQueryObject && workingQueryObject[propertyKey]) {
			if (workingQueryObject[propertyKey].queryOptionsType !== 'input' || workingQueryObject[propertyKey].key !== inputKey) {
				paginationQuery.placeholderQuery = workingQueryObject[propertyKey];
			}
		}
		return {
			...(workingQueryObject || {}),
			[propertyKey]: paginationQuery
		}
	}

	filterInvalidQueryResults(queryResults: Record<string, any>) {
		// Checking to see if the search property is valid within the queryResults
		if (queryResults.search) {
			queryResults.search = queryResults.search.reduce((items, item) => {
				if (item.matchtype && item.property && item.values) {
					const updatedItem = { ...item };
					const values = updatedItem.values;
					if (!common.commonUtils.isArrayLike(values)) {
						updatedItem.values = [values];
					}
					updatedItem.values = updatedItem.values.reduce((values, value) => {
						if (!common.commonUtils.isNullOrUndefined(value)) {
							if (common.commonUtils.isArrayLike(value)) {
								values.push(...value);
							} else {
								if (typeof value !== 'string') {
									values.push(value.toString());
								} else {
									values.push(value);
								}
							}
						}
						return values;
					}, []);
					items.push(updatedItem);
				}
				return items;
			}, []);
		}

		// Checking to see if the identifierSearch property is valid within the queryResults
		if (queryResults.identifierSearch) {
			const { identifierSearch } = queryResults;
			const { fuzzy, properties, value } = identifierSearch;
			if (!(fuzzy && properties && value)) {
				delete queryResults.identifierSearch;
			}
		}
		return queryResults;
	}

	cleanSearchEntityRequestBody(
		body: ISearchEntityRequest | ISearchGroupedEntityRequest,
		inputs: Record<string, any>,
		fetchOptions?: IFetchEntitiesOptions,
	): ISearchEntityRequest | ISearchGroupedEntityRequest {
		const response: ISearchEntityRequest = QueryContext.replaceInputs(body, inputs);
		if (response.search) {
			if (fetchOptions) {
				const { search: searchOptions = {} } = fetchOptions;
				const { strip = {} } = searchOptions;
				if (strip.blankStrings) {
					response.search.forEach((search) => {
						search.values = search.values.filter((value: string) => value !== '');
					});
				}
				if (strip.undefinedValues) {
					response.search.forEach((search) => {
						search.values = search.values.filter((value: string) => !common.commonUtils.isUndefined(value));
					});
				}
				if (strip.nullValues) {
					response.search.forEach((search) => {
						search.values = search.values.filter((value: string) => value !== null);
					});
				}
				if (strip.emptyValuesArrays) {
					response.search = response.search.filter(
						(search: ISearch) => search.values && search.values.length > 0,
					);
				}
			}
		}
		return response;
	}
	async fetchSavedSearchEntity(
		client: IQueryContextClient,
		configuration: IQueryContextConfigurationSavedSearchEntity,
		inputs: Record<string, any>,
		culture?: IQueryContextCulture,
	) {
		const queryString = {};
		if (culture) {
			const { sourceCulture, targetCulture } = culture;
			if (sourceCulture && targetCulture) {
				queryString['sourceCulture'] = sourceCulture;
				queryString['culture'] = targetCulture;
			}
		}
		for (const input of configuration.inputs) {
			const savedSearchInputKey = input.savedSearchInputKey || input.appInputKey;
			queryString[savedSearchInputKey] = inputs[input.appInputKey];
		}
		const response = await client.entity.savedSearchEntity.execute({
			queryString,
			urlParams: [configuration.key],
			method: 'GET',
			signal: this.abortController?.signal,
		});
		return response;
	}

	async fetchScenarios(
		client: IQueryContextClient,
		configuration: IQueryContextScenarios,
	): Promise<IServiceResponse<TScenarioListResponse>> {
		if (client.scenario && client.scenario.list) {
			const limit = (configuration && configuration.limit) || 10;
			const response = await client.scenario.list.execute({
				body: {
					paging: {
						limit,
					},
				},
				signal: this.abortController?.signal,
			});
			return response;
		}
		return {
			valid: false,
			errorCode: ErrorCode.exceptionCaught,
			error: 'Client missing scenario or scenario.list',
		};
	}

	async fetchScenarioReports(
		client: IQueryContextClient,
		configuration: IQueryContextScenarioReports,
	): Promise<IServiceResponse<TScenarioReportListResponse>> {
		if (client.scenarioReport && client.scenarioReport.list) {
			const limit = (configuration && configuration.limit) || 10;
			const response = await client.scenarioReport.list.execute({
				body: {
					paging: {
						limit,
					},
				},
				signal: this.abortController?.signal,
			});
			return response;
		}
		return {
			valid: false,
			errorCode: ErrorCode.exceptionCaught,
			error: 'Client missing scenario report or scenarioReport.list',
		};
	}

	async fetchDataset(
		client: IQueryContextClient,
		configuration: IQueryContextDataset,
		executionOptions: IExecutionOptions,
		staticInputs?: Record<string, any>,
		overrideDatasetRequest?: IDatasetExecuteRequestConfiguration
	): Promise<IDatasetExecuteResponse> {
		const { datasetKey, request: rawRequestConfig = {}, method = 'GET' } = configuration;
		if (client.datasets) {
			const { inputs: inputsConfig, ...rawRequest } = rawRequestConfig;

			// if the optional request body is not defined, then default to fetching based on the required datasetKey (which is used for context reference in queries).
			const request: IDatasetExecuteRequest = {
				code: datasetKey,
				...rawRequest,
				...overrideDatasetRequest
			};

			// convert request input queries into a key value object
			if (inputsConfig) {
				const inputQueryKeyValue = inputsConfig.reduce(
					(inputQueries: IQueryKeyValue, inputQuery: IQueryContextInputQuery) => {
						if (!inputQueries[inputQuery.key]) {
							inputQueries[inputQuery.key] = inputQuery.query;
						}
						return inputQueries;
					},
					{} as IQueryKeyValue,
				);
				let inputs = query.queryUtils.queryHelper.convertQueryKeyValue(
					{ queryClass: query.Query },
					inputQueryKeyValue,
					executionOptions,
					undefined,
					true,
				);
				if (staticInputs) {
					inputs = common.commonUtils.deepMergeObjects(inputs, staticInputs);
				}
				request.inputs = inputs;
			}

			if (method === 'GET' && client.datasets.executeGet) {
				const response = await client.datasets.executeGet.execute({
					queryString: request.inputs,
					urlParams: [request.code],
					signal: this.abortController?.signal,
				});
				return response;
			}
			if (method === 'POST' && client.datasets.execute) {
				const response = await client.datasets.execute.execute({
					body: {
						...request,
					},
					signal: this.abortController?.signal,
				});
				return response;
			}
		}
	}

	getQueryStringForPortfolioBaseRequest(configuration: IQueryContextPortalBase): any | undefined {
		const { entityInclude, riskEntity, entitySort, correlationIndicesOptions } = configuration;
		let queryString: undefined | any;
		if (entityInclude || riskEntity || entitySort) {
			queryString = {};
			if (entityInclude) {
				queryString.entityInclude = JSON.stringify(entityInclude);
			}
			if (riskEntity) {
				queryString.riskEntity = JSON.stringify(riskEntity);
			}
			if (entitySort) {
				queryString.entitySort = JSON.stringify(entitySort);
			}
			if (correlationIndicesOptions) {
				queryString.correlationIndicesOptions = JSON.stringify(correlationIndicesOptions);
			}
		}
		return queryString;
	}

	async fetchScenario(
		client: IQueryContextClient,
		inputs: Record<string, any>,
		configuration: IQueryContextScenario,
	): Promise<IServiceResponse<TScenarioGetResponse>> {
		if (client.scenario && client.scenario.get && configuration.id) {
			const idQuery = new query.Query(configuration.id);
			const id = idQuery.execute({ inputs });
			const queryString = this.getQueryStringForPortfolioBaseRequest(configuration);
			const response = await client.scenario.get.execute({
				queryString,
				urlParams: [id],
				signal: this.abortController?.signal,
			});
			return response;
		}
		return {
			valid: false,
			errorCode: ErrorCode.exceptionCaught,
			error: 'Client missing scenario or scenario.get or id in configuration',
		};
	}

	async fetchScenarioReport(
		client: IQueryContextClient,
		inputs: Record<string, any>,
		configuration: IQueryContextScenarioReport,
	): Promise<IServiceResponse<TScenarioReportGetResponse>> {
		if (client.scenarioReport && client.scenarioReport.get && configuration.id) {
			const idQuery = new query.Query(configuration.id);
			const id = idQuery.execute({ inputs });
			const queryString = this.getQueryStringForPortfolioBaseRequest(configuration);
			const response = await client.scenarioReport.get.execute({
				queryString,
				urlParams: [id],
				signal: this.abortController?.signal,
			});
			return response;
		}
		return {
			valid: false,
			errorCode: ErrorCode.exceptionCaught,
			error: 'Client missing scenario report or scenarioReport.get or id in configuration',
		};
	}

	async fetchPortfolio(
		client: IQueryContextClient,
		inputs: Record<string, any>,
		configuration: IQueryContextPortfolio,
	): Promise<IServiceResponse<TPortfolioGetResponse>> {
		if (client.portfolio && client.portfolio.get && configuration.id) {
			const idQuery = new query.Query(configuration.id);
			const id = idQuery.execute({ inputs });
			const queryString = this.getQueryStringForPortfolioBaseRequest(configuration);
			const response = await client.portfolio.get.execute({
				queryString,
				urlParams: [id],
				signal: this.abortController?.signal,
			});
			return response;
		}
		return {
			valid: false,
			errorCode: ErrorCode.exceptionCaught,
			error: 'Client missing portfolio or portfolio.get or id in configuration',
		};
	}

	async fetchPortfolioEntity(
		client: IQueryContextClient,
		inputs: Record<string, any>,
		configuration: IQueryContextPortfolio,
	): Promise<IServiceResponse<TPortfolioEntityGetResponse>> {
		if (client.portfolioEntity && client.portfolioEntity.get && configuration.id) {
			const idQuery = new query.Query(configuration.id);
			const id = idQuery.execute({ inputs });
			const queryString = this.getQueryStringForPortfolioBaseRequest(configuration);
			const response = await client.portfolioEntity.get.execute({
				queryString,
				urlParams: [id],
				signal: this.abortController?.signal,
			});
			return response;
		}
		return {
			valid: false,
			errorCode: ErrorCode.exceptionCaught,
			error: 'Client missing portfolioEntity or portfolioEntity.get or id in configuration',
		};
	}

	async fetchArticle(
		client: IQueryContextClient,
		inputs: Record<string, any>,
		configuration: IQueryContextLearning,
	): Promise<IGetArticleResponse | undefined> {
		if (client.learning && client.learning.getArticle && configuration.article && configuration.article.id) {
			const idQuery = new query.Query(configuration.article.id);
			const id = idQuery.execute({ inputs });
			const response = await client.learning.getArticle.execute({
				body: {
					id,
				},
				signal: this.abortController?.signal,
			});
			return response;
		}
		return;
	}

	async fetchCourses(
		client: IQueryContextClient,
		inputs: Record<string, any>,
		configuration: IQueryContextLearning,
	): Promise<IListMyCoursesResponse | undefined> {
		if (client.learning && client.learning.listMyCourses && configuration.courses) {
			const response = await client.learning.listMyCourses.execute({
				method: 'GET',
				signal: this.abortController?.signal,
			});
			return response;
		}
		return;
	}

	async fetchWordpressGlobalSearchContent(
		client: IQueryContextClient,
		inputs: Record<string, any>,
		configuration: IQueryContextGlobalSearchContent,
	): Promise<GlobalSearchResponse | undefined> {
		if (client.websites && client.websites.globalSearch && configuration.request) {
			const response = await client.websites.globalSearch.execute({
				method: 'GET',
				signal: this.abortController?.signal,
			});
			return response;
		}
		return;
	}

	async fetchLoggedInUser(
		client: IQueryContextClient,
		inputs: Record<string, any>,
		configuration: IQueryContextLoggedInUser,
	): Promise<IUser | undefined> {
		if (client.auth && client.auth.getUserByToken && configuration) {
			const response = await client.auth.getUserByToken.execute({ signal: this.abortController?.signal });
			return response;
		}
		return;
	}

	static replaceInputs(instance: any, inputs: Record<string, any>, keyPrefix = 'input') {
		// If there are multiple inputs then we would rather convert everything to a string once,
		// then replace it as a string search to avoid racking up the call stack
		try {
			let instanceString = JSON.stringify(instance);
			for (const key of Object.keys(inputs)) {
				const inputKey = `{${keyPrefix}:${key}}`;
				const inputValue = inputs[key];
				instanceString = instanceString.split(inputKey).join(inputValue);
			}
			const response = JSON.parse(instanceString);
			return response;
		} catch (ex) {
			// If the conversions fail for any reason we can do a deep recursion
			// This can fail if the configuration for some reason has a circular dependency, of the
			// input value is invalid when replacing into the string
			console.warn('replaceInputs: string replacement failed, moving to recursive', ex);
		}

		const workingInstance = common.commonUtils.deepMergeObjects({}, instance);
		for (const key of Object.keys(inputs)) {
			const inputKey = `{${keyPrefix}:${key}}`;
			const inputValue = inputs[key];
			replaceTextRecursive(workingInstance, inputKey, inputValue);
		}
		return workingInstance;
	}
}
